import React, { useEffect, useMemo, useRef } from "react";
import { Select, StaticInput, Input, Row, Col } from "@administrate/piston-ux";
import { useFormContext } from "@administrate/piston-ux/lib/Form";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";

import { CustomViewerFields } from "../types/Portal";
import { useTranslation } from "react-i18next";
import { usePortal } from "../providers/PortalProvider";
import {
  allowedOptions,
  CustomFieldDependencyRule,
  permittedValueMessage,
} from "../utils/customFieldDependencies";

type ContactDetailFormProps = {
  customViewerFields: Array<CustomViewerFields>;
  isCustomViewerFieldsLoading: boolean;
};

export enum StaticContactInputs {
  firstName,
  lastName,
  emailAddress,
  jobRole,
}

/** Where to read a field's parent value from the form, and the rules to apply. */
type FieldDependency = {
  parentKey: string;
  rules: readonly CustomFieldDependencyRule[];
};

const EMPTY: Map<string, FieldDependency> = new Map();

/**
 * The dependencies of the fields on this form, keyed by definition key, plus
 * enforcement of the chain: changing a parent's value clears any descendant
 * whose own value that parent no longer permits.
 *
 * A field's form name is its `definitionKey`, and `dependency.parentField` is a
 * sibling definition's key in the same id space, so a parent is found by name
 * with no conversion.
 *
 * Empty unless the instance has Custom Field dependencies enabled, which leaves
 * every field offering its own options untouched.
 */
const useCustomFieldDependencies = (
  fields: Array<CustomViewerFields>,
  enabled: boolean,
): Map<string, FieldDependency> => {
  const { values } = useFormContext();

  // `customViewerFields` is a fresh array on any render where the query has not
  // answered, so its identity cannot gate anything. Its content can - and the
  // rules belong in it, not just the keys: a re-fetch can rewrite a rule while
  // the field set is unchanged, and narrowing by the old rules would then
  // disagree with the write guard.
  const fieldsKey = JSON.stringify(
    fields.map(({ definitionKey, dependency }) => [definitionKey, dependency]),
  );

  const dependencies = useMemo(() => {
    if (!enabled) return EMPTY;

    const rendered = new Set(fields.map(({ definitionKey }) => definitionKey));
    const collected = new Map<string, FieldDependency>();

    fields.forEach(({ definitionKey, dependency }) => {
      // Fail open when the parent is not on this form. There is no parent value
      // to resolve, so the canonical rule would narrow the child to nothing and
      // block an edit the write guard would have accepted, because the guard
      // resolves that parent from the stored values instead.
      if (!dependency || !rendered.has(dependency.parentField)) return;

      collected.set(definitionKey, {
        parentKey: dependency.parentField,
        rules: dependency.rules,
      });
    });

    return collected;
    // `fields` is rebuilt per render; `fieldsKey` is its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fieldsKey]);

  // The reaction below outlives the render that registered it, so it reads the
  // options from here rather than from the array it closed over.
  const fieldsRef = useRef(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  });

  useEffect(() => {
    if (dependencies.size === 0) return;

    const dependents = [...dependencies];

    // A `reaction` rather than an `autorun` because an autorun also fires on
    // mount, which would clear a stored value that already violates its rules -
    // silent data loss for a learner who opens their profile and saves without
    // ever touching the field. The stored value survives; the control renders
    // blank, because narrowing removes it from the offered options, and the
    // write guard rejects it on save.
    //
    // Clearing a child changes a value the tracked expression reads, because a
    // child is the next level's parent, so the reaction fires again and the
    // chain clears one level per pass. It terminates because a value only ever
    // moves to null, and a null value is skipped.
    const parentValues = () =>
      dependents.map(([, { parentKey }]) => values.get(parentKey));

    // Only the dependents whose OWN parent moved are re-checked. Checking all of
    // them on any change clears an untouched field whose stored pair already
    // broke its rules - pick a new Colour and lose Model, on a form where the
    // two are unrelated. Every pass records what it saw, cascade passes
    // included, or a parent returning to a value it held before reads as
    // unmoved.
    let previous = parentValues();

    return reaction(parentValues, (current) => {
      const moved = dependents.filter(
        (_, index) => current[index] !== previous[index],
      );
      previous = current;

      moved.forEach(([key, { parentKey, rules }]) => {
        const field = fieldsRef.current.find(
          ({ definitionKey }) => definitionKey === key,
        );
        const value = field && values.get(key);
        if (!field || !value) return;

        const permitted = allowedOptions(
          rules,
          values.get(parentKey),
          field.options ?? [],
        );

        // Scalar compare: safe only because __unsupported_dependency_field_message
        // (dbal_custom_field_service.py) rejects multichoice on both sides of a
        // dependency. Permit a multichoice child there and this wipes its whole
        // selection on any parent change.
        if (!permitted.some((option) => option.value === value)) {
          values.set(key, null);
        }
      });
    });
  }, [dependencies, values]);

  return dependencies;
};

// `observer` because a child's option list is read from the form: without it a
// dropdown would keep offering what the parent's previous value permitted.
export const ContactDetailForm: React.FunctionComponent<
  React.PropsWithChildren<ContactDetailFormProps>
> = observer(({ customViewerFields, isCustomViewerFieldsLoading }) => {
  const { t } = useTranslation();
  const { profileSettings, customFieldDependenciesEnabled } = usePortal();
  const { values } = useFormContext();
  const dependencies = useCustomFieldDependencies(
    customViewerFields,
    customFieldDependenciesEnabled,
  );
  return (
    <>
      <Row>
        <Col sm={6}>
          {profileSettings?.allowEditName ? (
            <Input label={t("firstName")} name="firstName" />
          ) : (
            <StaticInput label={t("firstName")} name="firstName" />
          )}
        </Col>
        <Col sm={6}>
          {profileSettings?.allowEditName ? (
            <Input label={t("lastName")} name="lastName" />
          ) : (
            <StaticInput label={t("lastName")} name="lastName" />
          )}
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <StaticInput label={t("emailAddress")} name="emailAddress" />
        </Col>
        <Col sm={6}>
          {profileSettings?.allowEditJobTitle ? (
            <Input label={t("jobRole")} name="jobRole" />
          ) : (
            <StaticInput label={t("jobRole")} name="jobRole" />
          )}
        </Col>
      </Row>
      <Row>
        {isCustomViewerFieldsLoading ? (
          <Col sm={12}>
            <span
              className="placeholder animated"
              style={{ fontSize: "18px" }}
            />
          </Col>
        ) : (
          customViewerFields.map(({ definitionKey, name, options, type }) => {
            switch (type) {
              case "string":
                return (
                  <Col sm={6} key={definitionKey}>
                    <Input
                      label={name}
                      type="text"
                      name={definitionKey}
                      valid={(v) => (!!v ? true : "")}
                    />
                  </Col>
                );
              case "choice": {
                const dependency = dependencies.get(definitionKey);
                const permitted = allowedOptions(
                  dependency?.rules,
                  dependency && values.get(dependency.parentKey),
                  options ?? [],
                );
                return (
                  <Col sm={6} key={definitionKey}>
                    <Select
                      label={name}
                      options={permitted}
                      name={definitionKey}
                      valid={(v) =>
                        !v
                          ? `${t("pleaseSelectValue")}`
                          : // Only a field its rules actually narrow. A parent
                            // link with no rules is not a restriction - the
                            // guard drops it before enforcing - and any other
                            // field would report invalid whenever its stored
                            // value names an option somebody deleted.
                            !dependency?.rules.length ||
                            permittedValueMessage(permitted, v, t)
                      }
                    />
                  </Col>
                );
              }
              // TODO: add further input types when supported
              default:
                return null;
            }
          })
        )}
      </Row>
    </>
  );
});
